import { useEffect, useState } from "react";
import { useSearchParams, useLocation, Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2, MailQuestion, KeyRound, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Public-facing account flows:
 *   ForgotPassword — enter email, we email a reset link (200 either way)
 *   ForgotEmail    — enter phone or employee code, we email the address
 *   ResetPassword  — used by BOTH /reset-password AND /set-password routes;
 *                    validates the token, then lets the user choose a new
 *                    password.
 *
 * Every card mirrors the two-column hero layout of the Login page so the
 * flow feels continuous (no jarring context switch between screens).
 */

// ─────────────────────────────────────────────────────────────
// Shared layout — same hero as Login
// ─────────────────────────────────────────────────────────────
function AuthShell({ eyebrow, title, subtitle, icon: Icon, children, testId }) {
  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background" data-testid={testId}>
      <div className="hidden lg:flex relative overflow-hidden grain"
           style={{ background: "hsl(20 12% 10%)" }}>
        <img
          src="https://images.unsplash.com/photo-1743356629215-abb470447e71?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Coffee roastery"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-[hsl(36,25%,96%)] w-full">
          <img
            src="https://www.brotherscoffee.co.za/BCR_W%202026.png"
            alt="Brothers Coffee Roastery"
            className="h-16 w-auto self-start object-contain"
            style={{ maxWidth: "min(420px, 70%)" }}
          />
          <div className="max-w-md">
            <p className="label-tiny mb-3" style={{ color: "hsl(25 38% 62%)" }}>
              Manufacturing Suite
            </p>
            <h1 className="text-4xl md:text-5xl font-display font-bold leading-[1.05] tracking-tight">
              Account help
            </h1>
            <p className="mt-5 text-base text-[hsl(36,25%,82%)] leading-relaxed">
              We&apos;ll get you back into your account in a couple of taps.
              Check your inbox — links expire after 24 hours.
            </p>
          </div>
          <p className="text-xs" style={{ color: "hsl(25 8% 55%)" }}>
            © Brothers Coffee Roastery — Internal use
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-6 inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary/10 text-primary">
            <Icon className="w-5 h-5" strokeWidth={1.7} />
          </div>
          {eyebrow && <p className="label-tiny mb-2">{eyebrow}</p>}
          <h2 className="text-3xl font-display font-bold mb-1 tracking-tight">{title}</h2>
          {subtitle && <p className="text-sm text-muted-foreground mb-8">{subtitle}</p>}
          {children}
          <div className="mt-8 text-xs text-muted-foreground">
            <Link to="/login" className="hover:text-primary underline-offset-2 hover:underline" data-testid="back-to-login">
              ← Back to sign-in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const INPUT_CLS = "w-full px-3 py-2.5 bg-white border border-border rounded outline-none focus:border-primary focus:ring-1 focus:ring-primary text-sm";
const BTN_CLS = "w-full py-2.5 px-4 bg-primary text-primary-foreground rounded text-sm font-semibold tracking-wide hover:bg-[hsl(20,41%,32%)] transition-colors disabled:opacity-60";

// ─────────────────────────────────────────────────────────────
// ForgotPassword
// ─────────────────────────────────────────────────────────────
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/account/forgot-password", { email: email.trim() });
    } catch {
      // Endpoint returns 200 even if not found — any error here is
      // network-level. Still show the same success state for enum safety.
    }
    setSubmitting(false);
    setSent(true);
  };

  return (
    <AuthShell
      eyebrow="Account recovery"
      title="Forgot password"
      subtitle="Enter the email on your account and we'll send a reset link."
      icon={KeyRound}
      testId="forgot-password-page"
    >
      {sent ? (
        <SuccessPanel
          title="Check your inbox"
          testId="forgot-password-sent"
          message={<>If an account exists for <strong>{email}</strong>, we&apos;ve sent a link to reset the password. The link expires in 24 hours.</>}
        />
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-password-form">
          <div>
            <label className="label-tiny mb-1.5 block" htmlFor="fp-email">Email</label>
            <input
              id="fp-email" type="email" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className={INPUT_CLS} data-testid="forgot-password-email-input"
            />
          </div>
          <button type="submit" disabled={submitting || !email} className={BTN_CLS} data-testid="forgot-password-submit">
            {submitting ? "Sending…" : "Send reset link"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

// ─────────────────────────────────────────────────────────────
// ForgotEmail
// ─────────────────────────────────────────────────────────────
export function ForgotEmail() {
  const [identifier, setIdentifier] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/account/forgot-email", { identifier: identifier.trim() });
    } catch { /* enum-safe: always show success */ }
    setSubmitting(false);
    setSent(true);
  };

  return (
    <AuthShell
      eyebrow="Account recovery"
      title="Forgot your email"
      subtitle="Give us your phone number or employee code — we'll email the address on file."
      icon={MailQuestion}
      testId="forgot-email-page"
    >
      {sent ? (
        <SuccessPanel
          title="Check your inbox"
          testId="forgot-email-sent"
          message={<>If we recognise <strong>{identifier}</strong>, we&apos;ve emailed the account holder with the address on file.</>}
        />
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-email-form">
          <div>
            <label className="label-tiny mb-1.5 block" htmlFor="fe-ident">Phone number or employee code</label>
            <input
              id="fe-ident" type="text" required autoFocus
              value={identifier} onChange={(e) => setIdentifier(e.target.value)}
              placeholder="e.g. +27 82 123 4567 or OP-042"
              className={INPUT_CLS} data-testid="forgot-email-identifier-input"
            />
          </div>
          <button type="submit" disabled={submitting || !identifier} className={BTN_CLS} data-testid="forgot-email-submit">
            {submitting ? "Sending…" : "Email me my address"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

// ─────────────────────────────────────────────────────────────
// ResetPassword — powers both /reset-password AND /set-password
// ─────────────────────────────────────────────────────────────
export function ResetPassword() {
  const loc = useLocation();
  const isSetup = loc.pathname === "/set-password";
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [tokenState, setTokenState] = useState({ status: "checking" });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // {email}

  useEffect(() => {
    if (!token) {
      setTokenState({ status: "error", message: "Missing token." });
      return;
    }
    api.get(`/account/reset-token/${encodeURIComponent(token)}`)
      .then((r) => setTokenState({ status: "ok", ...r.data }))
      .catch((e) => setTokenState({
        status: "error",
        message: formatApiError(e.response?.data?.detail) || "Invalid link.",
      }));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pw2) { setError("The two passwords don't match."); return; }
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/account/reset-password", { token, new_password: pw });
      setDone({ email: data.email });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Failed to reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  const title = isSetup ? "Set your password" : "Reset your password";
  const eyebrow = isSetup ? "Welcome" : "Account recovery";
  const cta = isSetup ? "Set password" : "Update password";

  return (
    <AuthShell
      eyebrow={eyebrow} title={title}
      subtitle={done ? undefined : (isSetup ? "Choose a password to activate your account." : "Choose a new password to sign in with.")}
      icon={KeyRound} testId="reset-password-page"
    >
      {tokenState.status === "checking" && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="reset-checking">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking your link…
        </div>
      )}

      {tokenState.status === "error" && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive flex items-start gap-2" data-testid="reset-invalid">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">This link doesn&apos;t work</p>
            <p className="mt-1 text-destructive/80">{tokenState.message}</p>
            <p className="mt-3 text-muted-foreground">Ask your admin to resend the welcome email, or use <Link to="/forgot-password" className="underline underline-offset-2">Forgot password</Link>.</p>
          </div>
        </div>
      )}

      {tokenState.status === "ok" && !done && (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
          <p className="text-sm text-muted-foreground">
            For <strong className="text-foreground">{tokenState.email}</strong>
          </p>
          <div>
            <label className="label-tiny mb-1.5 block" htmlFor="pw1">New password</label>
            <input id="pw1" type="password" required minLength={8} autoComplete="new-password"
              value={pw} onChange={(e) => setPw(e.target.value)}
              className={INPUT_CLS} data-testid="reset-password-input" />
          </div>
          <div>
            <label className="label-tiny mb-1.5 block" htmlFor="pw2">Confirm</label>
            <input id="pw2" type="password" required minLength={8} autoComplete="new-password"
              value={pw2} onChange={(e) => setPw2(e.target.value)}
              className={INPUT_CLS} data-testid="reset-password-confirm-input" />
          </div>
          {error && <div className="text-sm text-destructive" data-testid="reset-error">{error}</div>}
          <button type="submit" disabled={submitting} className={BTN_CLS} data-testid="reset-password-submit">
            {submitting ? "Saving…" : cta}
          </button>
        </form>
      )}

      {done && (
        <SuccessPanel
          title={isSetup ? "You're all set" : "Password updated"}
          testId="reset-success"
          message={<>You can now sign in as <strong>{done.email}</strong>.</>}
          cta={<Link to="/login" className={`${BTN_CLS} block text-center mt-6`} data-testid="reset-to-login">Go to sign-in</Link>}
        />
      )}
    </AuthShell>
  );
}

// ─────────────────────────────────────────────────────────────
// Shared success card
// ─────────────────────────────────────────────────────────────
function SuccessPanel({ title, message, cta, testId }) {
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 flex items-start gap-2" data-testid={testId}>
      <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0 text-emerald-600" />
      <div className="flex-1">
        <p className="font-medium">{title}</p>
        <p className="mt-1 text-emerald-900/90">{message}</p>
        {cta}
      </div>
    </div>
  );
}

// Default export lets us also lazy-import this file cleanly if needed.
export default { ForgotPassword, ForgotEmail, ResetPassword, Mail };
